import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConnexioLogo } from '@/components/ConnexioLogo';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { useLocale } from '@/hooks/useLocale';
import { cn } from '@/lib/utils';
import styles from './PublicMarketingShell.module.css';

export const MARKETING_MAIN_ID = 'marketing-content';

export interface MarketingNavSection {
  id: string;
  label: string;
}

interface PublicMarketingShellProps {
  /** In-page anchors rendered in the header nav. */
  sections: MarketingNavSection[];
  /** Primary calls to action, rendered at the end of the top bar. */
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Logo destination. Showcase stays on `/product`; the home page uses `/`. */
  brandTo?: string;
  /** Optional caption beside the logo. Falls back to the showcase tagline. */
  brandTagline?: string;
  /** Accessible name for the in-page section nav. */
  navLabel?: string;
  /** Replaces the default login footer when provided. */
  footer?: ReactNode;
  /**
   * Drop the centred `max-w-7xl` column so sections can paint edge-to-edge
   * backgrounds and own their own inner container. Used by the sales page;
   * `/product` keeps the single measured column.
   */
  fullBleed?: boolean;
  /**
   * Put the section anchors in the top bar instead of a bordered second row.
   * Only viable for a handful of short labels — the showcase has eight
   * translated group names and needs the row.
   */
  inlineNav?: boolean;
  /** Extra classes on the outermost element, for surface token overrides. */
  surfaceClassName?: string;
  /**
   * Phone header for a page whose `actions` hold its primary call to action. Below
   * `md` the header is one row: logo mark, a short language toggle and `actions`;
   * sign in and the section anchors are left out (the page must offer sign in on
   * its first screen). Header controls are 44px tall below `lg` and 40px from `lg`.
   */
  compactActionsOnPhone?: boolean;
}

/**
 * Page shell for anonymous marketing surfaces. Sibling to `PublicDocumentShell`,
 * which is deliberately narrow (`max-w-3xl`) for quote and document views.
 */
export function PublicMarketingShell({
  sections,
  actions,
  children,
  className,
  brandTo = '/product',
  brandTagline,
  navLabel,
  footer,
  fullBleed = false,
  inlineNav = false,
  surfaceClassName,
  compactActionsOnPhone = false,
}: PublicMarketingShellProps) {
  const { t } = useTranslation('showcase');
  const { locale, changeLanguage } = useLocale();
  const nextLocale = locale === 'sv' ? 'en' : 'sv';
  const tagline = brandTagline ?? t('nav.brandTagline');

  const navList = (
    <ul className={styles.navList} data-inline={inlineNav ? '' : undefined}>
      {sections.map((section) => (
        <li key={section.id}>
          <Button
            asChild
            variant="secondary"
            size="sm"
            className={compactActionsOnPhone ? styles.compactButton : undefined}
          >
            <a href={`#${section.id}`}>{section.label}</a>
          </Button>
        </li>
      ))}
    </ul>
  );

  /* One nav element, reordered rather than duplicated: below lg it takes a full
     row of its own under the logo, at lg it sits inline beside it. Two nav
     elements toggled by breakpoint would be two landmarks with the same name. */
  const inlineNavElement = (
    <nav
      aria-label={navLabel ?? t('nav.label')}
      data-phone-hidden={compactActionsOnPhone ? '' : undefined}
      className={cn(styles.inlineNav, compactActionsOnPhone && styles.phoneHidden)}
    >
      {navList}
    </nav>
  );

  return (
    <div className={cn(styles.root, surfaceClassName)}>
      <a
        href={`#${MARKETING_MAIN_ID}`}
        className={cn('text-button', styles.skipLink)}
      >
        {t('skipToContent')}
      </a>

      <header className={styles.header}>
        <div
          data-inline-nav={inlineNav ? '' : undefined}
          className={styles.bar}
        >
          <Link
            to={brandTo}
            data-compact={compactActionsOnPhone ? '' : undefined}
            className={styles.brand}
          >
            {/* Compact: the wordmark stays as the link's accessible name but is not
                drawn below md, so mark, language and the trial button fit 320px. */}
            <ConnexioLogo
              showWordmark
              size={26}
              className={compactActionsOnPhone ? styles.compactLogo : undefined}
            />
            <Text
              textStyle="caption"
              tagName="span"
              className={styles.tagline}
            >
              {tagline}
            </Text>
          </Link>

          {inlineNav && sections.length > 0 ? inlineNavElement : null}

          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              lang={nextLocale}
              aria-label={t('nav.toggleLanguage')}
              className={compactActionsOnPhone ? styles.compactButton : undefined}
              onClick={() => void changeLanguage(nextLocale)}
            >
              {/* The long label ("Switch to English") plus the wordmark and Log in is wider
                  than a 320px screen, so every shell uses the short one below md. */}
              <span className={styles.langShort}>{t('nav.toggleLanguageShort')}</span>
              <span className={styles.langLong}>{t('nav.toggleLanguage')}</span>
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              data-phone-hidden={compactActionsOnPhone ? '' : undefined}
              className={
                compactActionsOnPhone ? cn(styles.compactButton, styles.loginCompact) : undefined
              }
            >
              <Link to="/login">{t('nav.login')}</Link>
            </Button>
            {actions}
          </div>
        </div>

        {/* Wraps rather than scrolls: eight translated group names do not fit one row at
            every width, and a clipped link a visitor has to scroll sideways to find is worse
            than a taller header. */}
        {inlineNav || sections.length === 0 ? null : (
          <nav aria-label={navLabel ?? t('nav.label')} className={styles.rowNav}>
            {navList}
          </nav>
        )}
      </header>

      <main
        id={MARKETING_MAIN_ID}
        data-measured={fullBleed ? undefined : ''}
        className={cn(styles.main, className)}
      >
        {children}
      </main>

      {footer ?? (
        <footer className={styles.footer}>
          <div className={styles.footerInner}>
            <Text textStyle="body.m" tagName="p">
              {t('footer.tagline')}
            </Text>
            <Text textStyle="body.m" tagName="p" className={styles.loginPrompt}>
              <span className={styles.muted}>{t('footer.loginPrompt')}</span>{' '}
              <Link
                to="/login"
                className={styles.loginLink}
              >
                {t('footer.loginLink')}
              </Link>
            </Text>
          </div>
        </footer>
      )}
    </div>
  );
}
